/**
 * Dog stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const dogStickersPack: ToilePack = {
    slug: 'dog-stickers',
    category: 'sticker',
    name: 'Dog stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'tilt', label: 'Head tilt', subject: 'a dog with its head tilted and one ear up, big eyes'},
        {key: 'zoomies', label: 'Zoomies', subject: 'a dog running flat out with speed lines and a flying tongue'},
        {key: 'bone', label: 'Puppy and bone', subject: 'a puppy sitting proudly beside a bone bigger than its head'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a golden retriever instead', 'for a dog leash', 'as a puppy version'],
    directionHint: 'try: a golden retriever instead',
    seo: {
        title: 'Dog Stickers: Head Tilt, Zoomies, Puppy',
        metaDescription: 'Nine dog stickers — a head tilt, the zoomies, a puppy with a bone — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Dog stickers: head tilt, zoomies, puppy and bone',
        tagline: 'Nine stickers, three dog moments, three styles each.',
        intro: 'Three moments every dog owner recognises, as die-cut stickers: a head tilt with one ear up, a full zoomies sprint with a flying tongue, and a puppy proud beside a bone bigger than its head. Each rendered kawaii, bold cartoon, and loose hand-drawn. Ask for a specific breed, a leash-and-walk scene, or a sleepy version, and the same three styles carry it.',
        useCases: [
            'a water bottle or laptop lid',
            'a leash or collar tag design',
            'a planner or journal page',
            'a gift for a dog owner',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine dog stickers — a head tilt, a zoomies sprint, and a puppy with a bone — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I get a specific breed?', a: 'Yes — the direction field takes any variation, including a specific breed, a leash-and-walk scene, or a sleepier version of any pose.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker up to a full A4 sheet.'},
            {q: 'Is this a photo of a real dog?', a: 'No — every sticker is an original cartoon illustration, not a photograph or a portrait of a specific dog.'},
        ],
    },
}
